import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import { getFileCategories, getSubFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import { listAllSubCategories, type SubCategoryListRow } from '../../services/caseRequestSubCategories';
import { MD_PERMISSIONS as MD } from '../../rbac/metadataPermissions';
import { PermissionGate } from '../../rbac/guards';
import { consumeFlashToast } from '../../utils/flashToast';
import { useAuth } from '../../contexts/AuthContext';

const EMPTY = '\u2014';

export default function SubCategoriesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SubCategoryListRow[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftSubCategoryId, setDraftSubCategoryId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subCategoryId, setSubCategoryId] = useState('');

  const [uiPage, setUiPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [draftSubCategories, setDraftSubCategories] = useState<LookupItem[]>([]);
  const [allSubCategories, setAllSubCategories] = useState<LookupItem[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [subOptionsLoading, setSubOptionsLoading] = useState(false);

  useEffect(() => {
    const flash = consumeFlashToast();
    if (flash?.message) {
      setToast(flash.message);
      window.setTimeout(() => setToast(null), 3200);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setOptionsLoading(true);
    void Promise.all([getFileCategories(), listAllSubCategories()]).then(([cats, allRes]) => {
      if (!alive) return;
      setFileCategories(cats.success ? cats.data : []);
      if (allRes.success) {
        const unique = new Map<string, LookupItem>();
        for (const row of allRes.data) {
          if (!unique.has(row.id)) {
            unique.set(row.id, { id: row.id, name: row.subCategoryName, code: row.subCategoryCode });
          }
        }
        setAllSubCategories(
          Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name)),
        );
      } else {
        setAllSubCategories([]);
      }
      setOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!draftCategoryId) {
      setDraftSubCategories([]);
      return;
    }
    let alive = true;
    setSubOptionsLoading(true);
    void getSubFileCategories(draftCategoryId, { useCache: false }).then((res) => {
      if (!alive) return;
      setDraftSubCategories(res.success ? res.data : []);
      setSubOptionsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [draftCategoryId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAllSubCategories({
        categoryId: categoryId || undefined,
        subCategoryId: subCategoryId || undefined,
      });
      if (!res.success) throw new Error(res.message);
      setRows(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('metadata.subCategories.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [categoryId, reloadToken, subCategoryId, t]);

  useEffect(() => {
    if (!user) return;
    void loadList();
  }, [user?.id, loadList]);

  const subCategoryFilterOptions = draftCategoryId ? draftSubCategories : allSubCategories;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(uiPage, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const columns = useMemo<Column<SubCategoryListRow>[]>(() => {
    return [
      {
        key: 'categoryName',
        header: t('admin.shared.fileCategory'),
        getValue: (r) => r.categoryName,
        render: (r) => <span style={{ fontWeight: 600 }}>{r.categoryName || EMPTY}</span>,
      },
      {
        key: 'subCategoryName',
        header: t('admin.shared.subFileCategory'),
        getValue: (r) => r.subCategoryName,
        render: (r) => r.subCategoryName,
      },
      {
        key: 'subCategoryCode',
        header: t('admin.shared.code'),
        getValue: (r) => r.subCategoryCode,
        render: (r) => <span style={{ color: 'var(--t2)' }}>{r.subCategoryCode || EMPTY}</span>,
      },
      {
        key: 'subCategoryActive',
        header: t('common.status'),
        getValue: (r) => (r.subCategoryActive ? t('common.active') : t('common.inactive')),
        render: (r) => (
          <span className={`al-pill al-pill-${r.subCategoryActive ? 'file-d' : 'user-d'}`}>
            {r.subCategoryActive ? t('common.active') : t('common.inactive')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
              <Link
                className="act-b"
                to={`/metadata/subcategories/${encodeURIComponent(r.id)}/edit`}
                data-tip={t('metadata.subCategories.updateSubCategory')}
                aria-label={t('metadata.subCategories.updateSubCategory')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            </PermissionGate>
          </div>
        ),
      },
    ];
  }, [t]);

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        {t('metadata.subCategories.title')}
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('metadata.subCategories.title')}</div>
        </div>
        <PermissionGate requirement={{ anyPermissions: [MD.MANAGE_MASTER_DATA] }}>
          <Link to="/metadata/subcategories/new" className="btn-create" style={{ textDecoration: 'none' }}>
            {t('metadata.subCategories.addSubCategory')}
          </Link>
        </PermissionGate>
      </div>

      {toast ? <div className="si-ok" style={{ marginBottom: 12 }}>{toast}</div> : null}

      <div className="flt-card">
        <div className="flt-grid">
          <LookupFieldSelect
            label={t('admin.shared.fileCategory')}
            value={draftCategoryId}
            placeholder={optionsLoading ? t('common.loading') : t('metadata.subCategories.selectFileCategory')}
            options={fileCategories.map((c) => ({ id: c.id, label: c.name }))}
            disabled={optionsLoading}
            emptyMessage={t('common.noDataFound')}
            onChange={(v) => {
              setDraftCategoryId(v);
              setDraftSubCategoryId('');
            }}
          />
          <LookupFieldSelect
            label={t('admin.shared.subFileCategory')}
            value={draftSubCategoryId}
            placeholder={
              optionsLoading || (draftCategoryId && subOptionsLoading)
                ? t('common.loading')
                : t('metadata.subCategories.selectSubFileCategory')
            }
            options={subCategoryFilterOptions.map((s) => ({ id: s.id, label: s.name }))}
            disabled={optionsLoading || Boolean(draftCategoryId && subOptionsLoading)}
            emptyMessage={t('common.noDataFound')}
            onChange={setDraftSubCategoryId}
          />
        </div>
        <div className="flt-actions">
          <button
            className="flt-btn"
            type="button"
            onClick={() => {
              setDraftCategoryId('');
              setDraftSubCategoryId('');
              setCategoryId('');
              setSubCategoryId('');
              setUiPage(1);
              setPageSize(10);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.reset')}
          </button>
          <button
            className="flt-btn pr"
            type="button"
            onClick={() => {
              setCategoryId(draftCategoryId);
              setSubCategoryId(draftSubCategoryId);
              setUiPage(1);
              setReloadToken((n) => n + 1);
            }}
          >
            {t('common.search')}
          </button>
        </div>
      </div>

      {error ? <div className="si-err">{error}</div> : null}

      <DataTable
        title={t('metadata.subCategories.tableTitle')}
        loading={loading}
        enableHeaderSearch={false}
        emphasizedHeader
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        defaultRowsPerPage={10}
        rowsPerPageOptions={[5, 10, 15, 25]}
        footerCountNoun={t('metadata.subCategories.footerCountNoun')}
        previousLabel={t('common.previous')}
        nextLabel={t('common.next')}
        serverPaging={{
          totalCount: rows.length,
          totalPages,
          page: safePage,
          pageSize,
          onPageChange: (p) => setUiPage(p),
          onPageSizeChange: (n) => {
            setPageSize(n);
            setUiPage(1);
          },
        }}
      />
    </>
  );
}
